

import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { CelebrationEvent } from "./celebration-provider";

interface AchievementToastProps {
  event: CelebrationEvent;
  onDismiss: () => void;
  style?: React.CSSProperties;
}

const typeConfig = {
  achievement: {
    gradient: "from-amber-500 via-yellow-500 to-orange-500",
    glow: "shadow-amber-500/50",
    defaultIcon: "🏆",
  },
  level_up: {
    gradient: "from-purple-500 via-violet-500 to-fuchsia-500",
    glow: "shadow-purple-500/50",
    defaultIcon: "⭐",
  },
  points: {
    gradient: "from-emerald-500 via-green-500 to-teal-500",
    glow: "shadow-emerald-500/50",
    defaultIcon: "💎",
  },
  personal_best: {
    gradient: "from-blue-500 via-cyan-500 to-sky-500",
    glow: "shadow-blue-500/50",
    defaultIcon: "🎯",
  },
  skill_unlocked: {
    gradient: "from-indigo-500 via-blue-500 to-violet-500",
    glow: "shadow-indigo-500/50",
    defaultIcon: "🔓",
  },
  skill_mastered: {
    gradient: "from-rose-500 via-pink-500 to-red-500",
    glow: "shadow-rose-500/50",
    defaultIcon: "🏅",
  },
};

export function AchievementToast({ event, onDismiss, style }: AchievementToastProps) {
  const config = typeConfig[event.type];
  const icon = event.icon || config.defaultIcon;

  return (
    <div
      className={cn(
        "pointer-events-auto animate-in slide-in-from-right-full fade-in duration-500",
        "relative overflow-hidden rounded-xl",
        "min-w-[320px] max-w-[400px]",
        "backdrop-blur-xl bg-white/90 dark:bg-gray-900/90",
        "border border-white/20",
        "shadow-2xl",
        config.glow
      )}
      style={style}
    >
      {/* Animated gradient border */}
      <div
        className={cn(
          "absolute inset-0 opacity-20",
          "bg-gradient-to-r",
          config.gradient,
          "animate-pulse"
        )}
      />

      {/* Top gradient bar */}
      <div
        className={cn(
          "absolute top-0 left-0 right-0 h-1",
          "bg-gradient-to-r",
          config.gradient
        )}
      />

      <div className="relative p-4 flex items-start gap-4">
        {/* Icon with glow effect */}
        <div
          className={cn(
            "flex-shrink-0 w-14 h-14 rounded-xl",
            "flex items-center justify-center",
            "bg-gradient-to-br",
            config.gradient,
            "shadow-lg",
            config.glow,
            "animate-bounce"
          )}
          style={{ animationDuration: "1s", animationIterationCount: "2" }}
        >
          <span className="text-2xl filter drop-shadow-lg">{icon}</span>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-gray-900 dark:text-white text-lg leading-tight">
            {event.title}
          </h4>
          {event.description && (
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5">
              {event.description}
            </p>
          )}
          {event.points && event.points > 0 && (
            <div
              className={cn(
                "inline-flex items-center gap-1 mt-2 px-2 py-1 rounded-full text-xs font-semibold",
                "bg-gradient-to-r",
                config.gradient,
                "text-white shadow-md"
              )}
            >
              <span>+{event.points}</span>
              <span>taškų</span>
            </div>
          )}
        </div>

        {/* Close button */}
        <button
          onClick={onDismiss}
          className="flex-shrink-0 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
        >
          <X className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      {/* Shimmer effect */}
      <div
        className="absolute inset-0 -translate-x-full animate-shimmer"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
          animationDuration: "2s",
          animationIterationCount: "infinite",
        }}
      />
    </div>
  );
}
